import { RiCheckLine } from 'react-icons/ri'
import { GButton } from 'components/basic-blocks'
import { ShopifyLinkStatusDTO } from 'services/user/ad-account-links'
import {
	InstructionList,
	SHOPIFY_COPY,
	ShopifyConnectLoading,
	StepRow,
	StepState,
	WaitingRow
} from './shopify-connect-content'
import { useShopifyConnect } from './use-shopify-connect'

interface ShopifyConnectSettingsProps {
	accountId: string
	assetId: string
	// Pre-fetched status (settings pages already know it). undefined → the hook self-fetches.
	initialLink?: ShopifyLinkStatusDTO | null
}

// Primary lives in flex-columns → self-start so it sizes to content instead of stretching full width.
const primaryBtn =
	'self-start rounded-[8px] px-6 h-[40px] min-w-[140px] shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)] border border-[#BAE100]'
const secondaryBtn = 'self-start rounded-[8px] px-4 h-[40px]'
const titleCls = 'text-base font-semibold'

/**
 * Settings-page Shopify connect wizard. Matches the server-side/DNS setup look (numbered step rows,
 * completed steps collapse to a green check, yellow primary + neutral secondary buttons). Also serves
 * the already-connected steady state — embed-off prompts to enable, expired access prompts to
 * reconnect — since the hook derives the step from the link.
 */
export const ShopifyConnectSettings = ({ accountId, assetId, initialLink }: ShopifyConnectSettingsProps) => {
	const s = useShopifyConnect({ accountId, assetId, initialLink })

	if (s.loading) return <ShopifyConnectLoading />

	// Final — connected + embed on.
	if (s.step === 'connected' && s.link) {
		return (
			<div className="flex flex-col gap-3">
				<div className="flex items-center gap-3">
					<RiCheckLine className="h-6 w-6 flex-shrink-0 text-green-500" />
					<span className="text-base font-medium text-green-600">{SHOPIFY_COPY.connected.title}</span>
				</div>
				<p className="text-base text-t-checkbox-unactive">{SHOPIFY_COPY.connected.blurb(s.link.shop_domain)}</p>
				<GButton
					label="Disconnect"
					color="neutral"
					className={secondaryBtn}
					loading={s.busy}
					disabled={s.busy}
					onClick={s.disconnect}
				/>
				<p className="text-sm text-t-checkbox-unactive">
					Disconnecting stops tracking. To fully remove the app, uninstall ClickGUARD from your Shopify admin →
					Settings → Apps.
				</p>
			</div>
		)
	}

	// Steps 1 & 2 — install the app, then turn on the embed.
	const installState: StepState = s.step === 'install' ? 'active' : 'done'
	const embedState: StepState = s.step === 'connected' ? 'done' : s.step === 'embed' ? 'active' : 'todo'
	const installTitle = installState === 'done' ? SHOPIFY_COPY.install.done : SHOPIFY_COPY.install.title
	const embedTitle = embedState === 'done' ? SHOPIFY_COPY.embed.done : SHOPIFY_COPY.embed.title

	return (
		<div className="flex flex-col">
			<StepRow index={1} state={installState} title={installTitle} titleClassName={titleCls}>
				{installState === 'active' && (
					<div className="flex flex-col gap-3">
						<p className="text-base text-t-checkbox-unactive">
							{s.needsReauth ? SHOPIFY_COPY.install.reconnectBlurb : SHOPIFY_COPY.install.blurb}
						</p>
						{s.phase === 'installing' ? (
							<WaitingRow text={SHOPIFY_COPY.install.waiting} onCancel={s.cancel} />
						) : (
							<div className="flex flex-col gap-2">
								<div className="flex flex-wrap items-center gap-3">
									<GButton
										label={s.needsReauth ? SHOPIFY_COPY.install.reconnectButton : SHOPIFY_COPY.install.button}
										color="yellow"
										className={primaryBtn}
										loading={s.busy}
										disabled={s.busy}
										onClick={s.installApp}
									/>
								</div>
								{s.error && <p className="text-sm text-[color:var(--error-ink,#D92D20)]">{s.error}</p>}
							</div>
						)}
					</div>
				)}
			</StepRow>

			<StepRow index={2} state={embedState} isLast title={embedTitle} titleClassName={titleCls}>
				{embedState === 'active' && (
					<div className="flex flex-col gap-4">
						<InstructionList items={SHOPIFY_COPY.embed.steps} />
						<p className="rounded-lg bg-bg-plan-fill px-3 py-2 text-[13px] leading-[18px] text-t-checkbox-unactive">
							{SHOPIFY_COPY.embed.note}
						</p>
						{s.phase === 'enabling' ? (
							<WaitingRow text={SHOPIFY_COPY.embed.waiting} onCancel={s.cancel} />
						) : (
							<div className="flex flex-wrap items-center gap-3">
								<GButton label={SHOPIFY_COPY.embed.button} color="yellow" className={primaryBtn} onClick={s.enableEmbed} />
								<GButton
									label="Disconnect"
									color="neutral"
									className={secondaryBtn}
									loading={s.busy}
									disabled={s.busy}
									onClick={s.disconnect}
								/>
							</div>
						)}
					</div>
				)}
				{embedState === 'todo' && <p className="text-sm text-t-checkbox-unactive">{SHOPIFY_COPY.embed.todo}</p>}
			</StepRow>
		</div>
	)
}
